import styled from 'styled-components';

/**
 * The auth shell — a 50/50 split, form left, photograph right.
 *
 * The design file has no auth screen, so the split is kept as it shipped and
 * only its parts are rebuilt from the system. Two decisions worth recording:
 *
 * 1. The form sits on a **card**. It used to float on the bare canvas, which
 *    made it the only form in the product without a surface under it — every
 *    other one in the panel is a `.card`. The card also gives the left half a
 *    centre, which it did not have: the block was 370px pinned left of centre
 *    in a 640px column with a great deal of nothing above it.
 *
 * 2. The card is owned by the layout, not by the three pages. Login, forgot
 *    and change each had their own identical copy of the container, the title
 *    and the subtitle, and the three had already drifted. The layout draws the
 *    surface once and the pages contribute only their contents.
 */

export const Container = styled.div`
  display: flex;
  min-height: 100vh;
`;

/* Left half. The canvas the whole product is drawn on. */
export const PagesContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  padding: var(--s6) var(--s5);
  background: var(--canvas);
`;

/*
 * The column the card lives in. The one width in the shell that is not a
 * token: it is measured for a single form field plus its label, and nothing
 * else in the product is this shape.
 */
export const FormSlot = styled.div`
  width: 100%;
  max-width: 400px;
`;

export const Card = styled.div`
  padding: var(--s6);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  box-shadow: var(--sh-1);
`;

/* Right half. The photograph, under a --primary wash. */
export const LayoutContent = styled.div`
  position: relative;
  width: 50%;
  min-height: 500px;
  overflow: hidden;
  background-image: url(/images/auth-back.png);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
`;

export const ColorBack = styled.div`
  position: absolute;
  inset: 0;
  background: var(--primary);
  opacity: 0.72;
`;

/*
 * Two corner washes that lift the brand off the rock face. They were already
 * here; they are only retokenised.
 */
export const FadeTopRight = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, var(--primary), transparent 50%);
  opacity: 0.3;
  pointer-events: none;
`;

export const FadeBottomLeft = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at bottom left, var(--primary), transparent 50%);
  opacity: 0.5;
  pointer-events: none;
`;

export const LogoContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: var(--s6);
  height: 100%;
  padding: var(--s7) var(--s5);
`;

export const Logo = styled.img`
  width: 100%;
  max-width: 320px;
  max-height: 96px;
  object-fit: contain;
`;

export const WelcomeInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--s5);
`;

export const LockIcon = styled.img`
  width: 100%;
  max-width: 200px;
  max-height: 160px;
  object-fit: contain;
`;

/*
 * The only page-scale heading in the product — see --fs-display. The old
 * value was 22px/800, which was on no scale and heavier than anything else
 * anywhere.
 */
export const Title = styled.h2`
  font-family: var(--font-ui);
  font-size: var(--fs-display);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  letter-spacing: 0.02em;
  text-align: center;
  color: var(--text-invert);
`;
